/** Widget tool names that produce HTML frames instead of inline tool chips. */
export const WIDGET_TOOL_NAMES = new Set(['render_widget', 'render_interactive']);

export interface SseEvent {
  readonly event: string;
  readonly data: string;
}

export type SseHandler = (e: SseEvent) => void;

/* SSE messages are separated by \n\n. Parse the complete events out of the */
/* buffer, feed them to the handler, and return the unconsumed tail. */
export function feedSseBuffer(buffer: string, handler: SseHandler): string {
  const events = buffer.split(/\n\n/);
  const tail = events.pop() ?? '';
  for (const chunk of events) {
    if (!chunk.trim()) continue;
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of chunk.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }
    if (dataLines.length > 0) {
      handler({ event: eventName, data: dataLines.join('\n') });
    }
  }
  return tail;
}

/** Thrown by `assertSseResponse` when the HTTP response itself isn't SSE (the
 *  request failed before any streaming started — auth, validation, or an
 *  unhandled server error). Carries the status so callers can map it to a
 *  user-facing message without re-parsing the raw body. */
export class SseResponseError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

/** Assert that a fetch Response is OK and has a body, or throw a descriptive
 *  Error. Pass `funcName` for context in the error message (e.g. 'chat-respond').
 *  The caller can pass `res` directly after `await fetch(…)`. */
export async function assertSseResponse(res: Response, funcName: string): Promise<void> {
  if (!res.ok || !res.body) {
    const text = res.body ? await res.text() : '';
    /* Le handler d'erreur global (apps/api/src/middleware/error-handler.ts)
       rend `{"error":{"code":...,"message":...}}` — le message est IMBRIQUÉ,
       jamais à la racine. Ne lire que la racine faisait toujours échouer le
       parsing sur un 500 générique et retomber sur le texte brut affiché tel
       quel à l'utilisateur (NAI, capture Yassine 14/10). */
    let message: string | undefined;
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: { message?: string } };
      message = parsed.error?.message ?? parsed.message;
    } catch { /* not JSON */ }
    throw new SseResponseError(res.status, message ?? `${funcName} HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
}

/* Seuils de silence applicatif (aucun événement utile — delta/outil/widget —
   alors que le serveur garde la socket vivante par keepalive). Le premier
   n'est qu'une information ; le second est un vrai échec. Vertex a déjà
   répondu après plus de 120 s : l'abandon est volontairement très généreux
   (le timeout TTFB serveur de 15 s, #2194, avait été reverté, #2196). */
export const SLOW_RESPONSE_MS = 45_000;
export const SILENCE_ABORT_MS = 90_000;

export class SseSilenceError extends Error {
  constructor() {
    super('sse-silence-timeout');
  }
}

export interface SseWatch {
  /** Silence anormalement long : à afficher comme information, pas comme échec. */
  onSlow?: () => void;
  /** Un événement utile est arrivé : l'information de lenteur n'a plus lieu d'être. */
  onActive?: () => void;
  slowAfterMs?: number;
  abortAfterMs?: number;
}

/** Drain a ReadableStream<Uint8Array> as Server-Sent Events, calling `handler`
 *  for each complete event. Automatically flushes any trailing bytes when the
 *  stream closes. Callers only need to pass the response body + a handler —
 *  the read loop, TextDecoder, and buffer management are handled here.
 *  `readTimeoutMs` guards against a silent server that keeps the TCP socket
 *  open but stops writing — throws 'sse-read-timeout' if no chunk arrives
 *  within the deadline (default 30 s; the server heartbeat is 8 s).
 *  `watch` surveille en plus le silence APPLICATIF (keepalive exclus). */
export async function drainSseStream(
  body: ReadableStream<Uint8Array>,
  handler: SseHandler,
  readTimeoutMs = 30_000,
  watch?: SseWatch,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let slowTimer: ReturnType<typeof setTimeout> | undefined;
  let abortTimer: ReturnType<typeof setTimeout> | undefined;
  let rejectSilence: (e: Error) => void = () => {};
  const silence = new Promise<never>((_, rej) => { rejectSilence = rej; });
  silence.catch(() => {});

  const arm = () => {
    if (!watch) return;
    clearTimeout(slowTimer);
    clearTimeout(abortTimer);
    slowTimer = setTimeout(() => watch.onSlow?.(), watch.slowAfterMs ?? SLOW_RESPONSE_MS);
    abortTimer = setTimeout(() => rejectSilence(new SseSilenceError()), watch.abortAfterMs ?? SILENCE_ABORT_MS);
  };
  const watched: SseHandler = (e) => {
    if (e.event !== 'meta') {
      arm();
      watch?.onActive?.();
    }
    handler(e);
  };

  arm();
  try {
    for (;;) {
      let readTimer: ReturnType<typeof setTimeout> | undefined;
      const read = new Promise<{ done: boolean; value?: Uint8Array }>((res, rej) => {
        readTimer = setTimeout(() => rej(new Error('sse-read-timeout')), readTimeoutMs);
        reader.read().then((r) => { clearTimeout(readTimer); res(r); }, (e) => { clearTimeout(readTimer); rej(e); });
      });
      const result = await Promise.race([read, silence]).catch((e) => { clearTimeout(readTimer); throw e; });
      if (result.done) break;
      buffer += decoder.decode(result.value, { stream: true });
      buffer = feedSseBuffer(buffer, watched);
    }
    if (buffer.trim()) feedSseBuffer(buffer + '\n\n', watched);
  } catch (e) {
    void reader.cancel().catch(() => {});
    throw e;
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(abortTimer);
  }
}
